import React from 'react';
import { motion } from 'framer-motion';
import { BookMarkedIcon, BookOpenIcon, BriefcaseIcon, CalendarIcon, CheckCircle2Icon, CompassIcon, EyeIcon, GlobeIcon, GraduationCapIcon, PenToolIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Container } from '../components/ui/Container';
import { GeometricAccent } from '../components/ui/GeometricAccent';
import { MosqueArchMask } from '../components/ui/MosqueArchMask';
import { PatternDecor } from '../components/ui/PatternDecor';
type Tone = 'sage' | 'sky' | 'rose' | 'sand';
type Icon = typeof CalendarIcon;
type Program = {
  icon: Icon;
  title: string;
  description: string;
  action: string;
  tone: Tone;
  to?: string;
};
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  sky: 'border-[#C7D4E0]/40 bg-[#EDF2F7]',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sand: 'border-sand-300/40 bg-sand-100'
};
const audiences = ['الآباء والأمهات والمهنيون في بداية مسيرتهم', 'أفراد المجتمع وقادته', 'المعلمون والمرشدون', 'رواد الأعمال والمبدعون'];
const programs: Program[] = [{
  icon: CalendarIcon,
  title: 'البرامج السنوية',
  description: 'زمالات ومسارات اعتماد تمتد لعام كامل، من أجل نمو منظّم وإرشاد مستمر.',
  action: 'استكشف',
  tone: 'sage',
  to: '/ar/adults/annual-programs'
}, {
  icon: BookOpenIcon,
  title: 'الدورات الأساسية',
  description: 'دورات قصيرة تطبيقية في الهوية والحياة الأسرية والاندماج والأخلاق وأساليب الحياة اليومية للمسلم.',
  action: 'تصفّح',
  tone: 'sky',
  to: '/ar/adults/core-courses'
}, {
  icon: BriefcaseIcon,
  title: 'التطوير المهني',
  description: 'برامج لاعتماد المعلمين وتنمية قيادة المجتمع للمربين والمرشدين والعاملين في المجتمع.',
  action: 'عرض',
  tone: 'rose',
  to: '/ar/adults/professional-development'
}, {
  icon: PenToolIcon,
  title: 'التعلّم الإبداعي',
  description: 'اللغة والثقافة والمهارات: العربية، ولغات الثقة، والخط، والفنون الإسلامية، ومهارات الحياة العملية.',
  action: 'اكتشف',
  tone: 'sand',
  to: '/ar/adults/creative-learning'
}];
const pillars = [{
  icon: CompassIcon,
  title: 'قيم إسلامية تطبيقية',
  description: 'فهم كيف يهدي الإسلام تفاصيل الحياة اليومية.',
  tone: 'sage' as Tone
}, {
  icon: EyeIcon,
  title: 'الاندماج والمهارات المهنية',
  description: 'العيش بثقة في المجتمعات غير المسلمة.',
  tone: 'rose' as Tone
}, {
  icon: GlobeIcon,
  title: 'الثقافة واللغة والإبداع',
  description: 'التراث مصدر قوة لا عزلة.',
  tone: 'sand' as Tone
}];
const featuredPrograms = [{
  icon: CompassIcon,
  title: 'زمالة الحياة المسلمة',
  description: 'رحلة تعليمية تطبيقية لعام كامل للمسلمين في الدول غير المسلمة، متجذرة في القيم الإسلامية وملائمة للواقع المعاصر.',
  tone: 'sage' as Tone
}, {
  icon: BookMarkedIcon,
  title: 'مسار القيادة المهنية للمسلمين',
  description: 'للمهنيين والقادة الذين يريدون القيادة بأخلاق والتواصل بفاعلية والحضور بثقة في المجالات العامة والمهنية.',
  tone: 'sky' as Tone
}, {
  icon: GraduationCapIcon,
  title: 'مسار المعلّم والمرشد',
  description: 'اعتماد للمعلمين والمرشدين الداعمين للأطفال والأسر المسلمة: نمو الطفل والحماية والتدريس الأخلاقي.',
  tone: 'rose' as Tone
}];
const formats = [{
  title: 'الدورات الأساسية',
  points: ['الهوية والقيم والأسرة والاندماج', '8 أسابيع', 'صيغة جماعية', 'الأساس لمسار البالغين'],
  tone: 'sage' as Tone
}, {
  title: 'دورات الإثراء',
  points: ['اللغات والخط والثقافة', '4 إلى 8 أسابيع', 'اختيارية أو إضافية'],
  tone: 'sky' as Tone
}, {
  title: 'الورش والمكثفات',
  points: ['صيغة عطلة نهاية الأسبوع', 'يوم إلى يومين', 'موضوعات عملية: المال والزواج والأعمال ومهارات الحياة'],
  tone: 'sand' as Tone
}];
export function ArabicAdults() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-2293" data-mp="uph29qp8lq9c">
      <section className="relative isolate overflow-hidden" data-id="element-2294" data-mp="prfl5bbdg10i">
        <PatternDecor side="left" y="middle" size="lg" data-id="element-2295" data-mp="pqwckl7m2oz5" />
        <GeometricAccent variant="arabesque" className="absolute -right-20 top-10 hidden h-80 w-80 md:block" opacity={0.12} data-id="element-2296" data-mp="1n7cz6gcyu9u6" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-2297" data-mp="1ifbec61qfasj">
          <div className="grid items-center gap-10 lg:grid-cols-12" data-id="element-2298" data-mp="12xk6z19mxika">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="lg:col-span-6" data-id="element-2299" data-mp="130g4wplhg78q">
              <p className="text-sm font-semibold text-forest-700" data-id="element-2300" data-mp="t1gtx0d8vzik">تعلّم تطبيقي للبالغين المسلمين في الدول غير المسلمة</p>
              <h1 className="mt-3 font-display text-5xl font-semibold leading-[1.4] text-ink-900 md:text-7xl" data-id="element-2301" data-mp="y1393fcmvpu0">برامج البالغين</h1>
              <div className="mt-6 max-w-xl space-y-4 leading-loose text-ink-700" data-id="element-2302" data-mp="1qnef55j0eiy7">
                <p data-id="element-2303" data-mp="1byrq2c3kycts">تدعم برامجنا البالغين المسلمين الذين يريدون العيش والعمل وبناء أسرهم بثقة ووضوح ومسؤولية، مع البقاء أوفياء لقيمهم وهويتهم.</p>
                <p data-id="element-2304" data-mp="1gwjuu6pbptfe">تركّز هذه البرامج على التطبيق الواقعي للمبادئ الإسلامية والأخلاق والمهارات المهنية. نحن نكمّل التعليم الديني التقليدي ولا نستبدله، من خلال مهارات حياة وتعلّم عملي يدعم المسلمين في المجتمعات المعاصرة.</p>
              </div>
              <p className="mt-6 font-semibold text-ink-900" data-id="element-2305" data-mp="29g0a7nm3e5hq">صُممت برامج البالغين من أجل:</p>
              <ul className="mt-3 space-y-2 text-ink-700" data-id="element-2306" data-mp="3u2hsqxperfvt">{audiences.map(item => <li key={item} className="flex items-center gap-3" data-id="element-2307" data-mp="3p4oq2enbd40a"><CheckCircle2Icon size={16} className="shrink-0 text-forest-700" strokeWidth={1.6} aria-hidden="true" data-id="element-2308" data-mp="7p01i3uq4dnf" /><span data-id="element-2309" data-mp="2q5awfqilksr">{item}</span></li>)}</ul>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-2310" data-mp="2r7zui4a1d61"><a href="#programs" data-id="element-2311" data-mp="2s9f6ysbsltk"><Button variant="primary" data-id="element-2312" data-mp="3b5gq3jjbtrv1">استكشف البرامج</Button></a><Link to="/ar/contact" data-id="element-2313" data-mp="3b5yskxwl12be"><Button variant="secondary" data-id="element-2314" data-mp="3b7jlkj390d75">تواصل معنا</Button></Link></div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-2315" data-mp="368ymwo3na1kt"><div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-2316" data-mp="3g3i4hr5t5306"><MosqueArchMask src="https://cdn.magicpatterns.com/uploads/4Uk2Rxk5Lbd1tQvC5K6BB6/Adults2.jpg" alt="مهنيون مسلمون يتعاونون حول حواسيب محمولة في مكتب حديث" data-id="element-2317" data-mp="zeuse4y8cbe7" /></div></motion.div>
          </div>
        </Container>
      </section>

      <section id="programs" className="scroll-mt-24 bg-sage-50/60 py-16 md:py-24" data-id="element-2318" data-mp="3obf4vxhyt00f"><Container data-id="element-2319" data-mp="3t6ksd0r0mmqm"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2320" data-mp="3ta8kpwkq533r">استكشف تعلّم البالغين</h2><div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-2321" data-mp="2000jet4zasj">{programs.map((program, index) => <ProgramCard key={program.title} program={program} index={index} data-id="element-2322" data-mp="61bwe22lss96" data-mpi={index} />)}</div></Container></section>

      <section className="bg-white/60 py-16 md:py-24" data-id="element-2323" data-mp="35ggoqtdtumzm"><Container data-id="element-2324" data-mp="3aguxyer602z8"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2325" data-mp="3aipgxnivll0o">كيف تعمل برامج البالغين</h2><p className="mt-5 leading-loose text-ink-700" data-id="element-2326" data-mp="3ehom998irjsn">تُبنى جميع البرامج حول ثلاثة محاور:</p><div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-2327" data-mp="3jargzkp18zaf">{pillars.map((pillar, index) => <PillarCard key={pillar.title} pillar={pillar} index={index} data-id="element-2328" data-mp="2msv7zh1ogglp" data-mpi={index} />)}</div><p className="mt-10 text-center text-sm leading-loose text-ink-500" data-id="element-2329" data-mp="2qtnhmbegonii">تُقدَّم البرامج عبر الإنترنت من خلال جلسات مباشرة وتفاعلية ضمن مجموعات مركّزة وورش عمل ديناميكية موجّهة.</p></Container></section>

      <section className="relative isolate overflow-hidden bg-[#F7EDE9]/60 py-16 md:py-24" data-id="element-2330" data-mp="2eb1j1qrm35np"><PatternDecor side="right" y="top" size="lg" data-id="element-2331" data-mp="2e98eqi4h0xiv" /><Container data-id="element-2332" data-mp="2a8eovj7gx8nc"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2333" data-mp="2a84wxq2v4rf0">برامج سنوية مميزة</h2><p className="mt-5 leading-loose text-ink-700" data-id="element-2334" data-mp="259uzf8to1eq8">رحلات سنوية صُممت للتعلّم التطبيقي والتأمل والنمو في الحياة الواقعية.</p><div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-2335" data-mp="20dd1arsucemv">{featuredPrograms.map((program, index) => <FeaturedCard key={program.title} program={program} index={index} data-id="element-2336" data-mp="1vj8ss43xfyjo" data-mpi={index} />)}</div></Container></section>

      <section className="bg-sand-50/60 py-16 md:py-24" data-id="element-2337" data-mp="1qje1w0k5j8ym"><Container data-id="element-2338" data-mp="1mkofjih4yh2u"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2339" data-mp="1mkwr971ivgcg">صيغ التعلّم</h2><div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-2340" data-mp="1hjh7rjezthw6">{formats.map((format, index) => <FormatCard key={format.title} format={format} index={index} data-id="element-2341" data-mp="1clfniv5tzcj4" data-mpi={index} />)}</div></Container></section>

      <section className="bg-white/60 py-16 md:py-24" data-id="element-2342" data-mp="30i5gxgadp6dp"><Container data-id="element-2343" data-mp="30gas3qvzku4c"><div className="space-y-5 leading-loose text-ink-700" data-id="element-2344" data-mp="30gkktm8f1fdq"><p data-id="element-2345" data-mp="35gfbpps6y4ys"><strong className="text-ink-900" data-id="element-2346" data-mp="35g6xnukc0tm0">برامج البالغين</strong> تقدّم تعليمًا تطبيقيًا في القيم والأخلاق والوعي الثقافي ومهارات الحياة، بما يدعم المشاركة الواثقة والمسؤولة للبالغين المسلمين في المجتمعات غير المسلمة.</p><p data-id="element-2347" data-mp="2wf8qj3elk5z7"><strong className="text-ink-900" data-id="element-2348" data-mp="2wf8sh8y5qg1u">المحتوى الإسلامي</strong> يُقدَّم بصورة سياقية وعملية ومحترمة للعلم الإسلامي التقليدي، مع التركيز على النمو الأخلاقي والمسؤولية الاجتماعية والاندماج الإيجابي.</p><p data-id="element-2349" data-mp="2mk72kaw259y7"><strong className="text-ink-900" data-id="element-2350" data-mp="2mk72ybzyzbyq">دورات الأعمال والتمويل</strong> تركّز على اتخاذ القرار الأخلاقي والشفافية والمشاركة الاقتصادية المسؤولة، ولا تقدّم استشارات مالية أو قانونية منظّمة.</p><p data-id="element-2351" data-mp="2ctuhmhkbzerj"><strong className="text-ink-900" data-id="element-2352" data-mp="2ctm7gxflenk1">البرامج الثقافية والإبداعية</strong> تعزّز تقدير التراث والتعبير عن الذات ومهارات المجتمع، وتدعم الاندماج والرفاه.</p></div></Container></section>

      <section className="relative isolate overflow-hidden bg-[#EDF2F7]/60 py-16 md:py-24" data-id="element-2353" data-mp="23secao8di2u6"><PatternDecor side="left" y="middle" size="md" data-id="element-2354" data-mp="1k5t5oea4ni40" /><Container data-id="element-2355" data-mp="1k6l4dxz0tvyi"><div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-2356" data-mp="1k8fe0g4ue1na"><div className="max-w-xl" data-id="element-2357" data-mp="1f8t1ya0r2rg2"><h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-2358" data-mp="1f9mb1hn6lt1d">ابدأ رحلة تعلّمك</h2><p className="mt-5 leading-loose text-ink-700" data-id="element-2359" data-mp="1p5fjopxs52dv">اختر برنامجًا سنويًا أو ابدأ بدورة مركّزة: تعلّم عملي وتطبيقي ومصمم للحياة المعاصرة.</p></div><div className="flex flex-wrap gap-3" data-id="element-2360" data-mp="2345iqb45tc4v"><a href="#programs" data-id="element-2361" data-mp="23346427hwhl1"><Button variant="primary" data-id="element-2362" data-mp="1y63znq6qhcte">تصفّح البرامج</Button></a><Link to="/ar/contact" data-id="element-2363" data-mp="1y5cg5dqev5jh"><Button variant="secondary" data-id="element-2364" data-mp="1y5u9a1hs13n6">اطلب معلومات</Button></Link></div></div></Container></section>
    </div>;
}
function ProgramCard({
  program,
  index
}: {
  program: Program;
  index: number;
}) {
  const Icon = program.icon;
  const content = <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} className={`flex h-full flex-col gap-4 rounded-2xl border p-6 shadow-soft md:p-7 ${toneClasses[program.tone]}`} data-id="element-2365" data-mp="2lvf35ffgxlb8"><span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-forest-700/15 bg-white text-forest-700" data-id="element-2366" data-mp="1vpu65mqkduo8"><Icon size={20} strokeWidth={1.5} aria-hidden="true" data-id="element-2367" data-mp="1qpzids235l7e" /></span><h3 className="font-display text-xl font-semibold leading-loose text-ink-900" data-id="element-2368" data-mp="1qoeo83joo4a2">{program.title}</h3><p className="text-[15px] leading-loose text-ink-700" data-id="element-2369" data-mp="1lptkhu5832gv">{program.description}</p><span className="mt-auto pt-1 text-sm font-medium text-forest-700" data-id="element-2370" data-mp="2jen5lt6eaknl">{program.action} <span aria-hidden="true" data-id="element-2371" data-mp="2ehmwtai5v3ss">←</span></span></motion.article>;
  return program.to ? <Link to={program.to} className="block h-full" data-id="element-2372" data-mp="29gh3bagofci7">{content}</Link> : content;
}
function PillarCard({
  pillar,
  index
}: {
  pillar: (typeof pillars)[number];
  index: number;
}) {
  const Icon = pillar.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08
  }} className={`rounded-2xl border p-6 text-center shadow-soft ${toneClasses[pillar.tone]}`} data-id="element-2373" data-mp="y34vkohsq9qr"><span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-2374" data-mp="39p5qg188fi6v"><Icon size={20} strokeWidth={1.5} aria-hidden="true" data-id="element-2375" data-mp="3engyq7ivpkmz" /></span><h3 className="mt-4 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-2376" data-mp="3jf8ju2j9my5q">{pillar.title}</h3><p className="mt-2 text-sm leading-loose text-ink-700" data-id="element-2377" data-mp="3oelevakajbje">{pillar.description}</p></motion.article>;
}
function FeaturedCard({
  program,
  index
}: {
  program: (typeof featuredPrograms)[number];
  index: number;
}) {
  const Icon = program.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08
  }} className={`rounded-2xl border p-6 shadow-soft ${toneClasses[program.tone]}`} data-id="element-2378" data-mp="1ax2x7kkhhlk"><span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-2379" data-mp="27ztlovfbm091"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-2380" data-mp="3bc0las26pvy8" /></span><h3 className="mt-5 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-2381" data-mp="36bw7j9snvfbp">{program.title}</h3><p className="mt-3 text-sm leading-loose text-ink-700" data-id="element-2382" data-mp="3kbfcjtq6eajs">{program.description}</p><span className="mt-4 inline-flex text-sm font-medium text-forest-700" data-id="element-2383" data-mp="3k9ayiiykbv5s">اعرف المزيد ←</span></motion.article>;
}
function FormatCard({
  format,
  index
}: {
  format: (typeof formats)[number];
  index: number;
}) {
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} className={`rounded-2xl border p-6 shadow-soft ${toneClasses[format.tone]}`} data-id="element-2384" data-mp="3parx27tkr5j7"><h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-2385" data-mp="2vpdlv01swg4">{format.title}</h3><ul className="mt-4 space-y-2 text-sm leading-loose text-ink-700" data-id="element-2386" data-mp="gvqf9co507wn">{format.points.map(point => <li key={point} className="flex items-center gap-2" data-id="element-2387" data-mp="bx6pf16fabz0"><span className="h-1.5 w-1.5 shrink-0 rounded-full bg-forest-700" aria-hidden="true" data-id="element-2388" data-mp="vhnq6nn7lnm9" />{point}</li>)}</ul></motion.article>;
}